import {
  FileOpenRounded as FileOpenRoundedIcon,
  TextSnippetRounded as TextSnippetRoundedIcon,
} from "@mui/icons-material";
import { Box, Link as MuiLink, Typography } from "@mui/material";
import Chip from "@mui/material/Chip";
import { GridColDef, GridRenderCellParams, GridSortModel } from "@mui/x-data-grid";

import { IGrantsData, ISecFilingsData, ISponsoredResearchData } from "@interfaces/company.type";

export const getSortModel = (selectedSource?: string): GridSortModel => {
  switch (selectedSource) {
    case "grants": {
      return [{ field: "awardYear", sort: "desc" }];
    }
    case "licensingDeals": {
      return [{ field: "licensorName", sort: "asc" }];
    }
    case "secFilings": {
      return [{ field: "fileDate", sort: "desc" }];
    }
    case "sponsoredResearch": {
      return [{ field: "year", sort: "desc" }];
    }
    default: {
      return [];
    }
  }
};

export const sponsoredColumns: GridColDef[] = [
  {
    field: "year",
    headerName: "Year",
    renderCell: (parameters) => <Typography variant="subtitle2">{parameters.value}</Typography>,
    valueGetter: (value) => Number.parseInt(value, 10),
    width: 100,
  },
  {
    field: "price",
    headerName: "Amount",
    renderCell: (parameters: GridRenderCellParams<ISponsoredResearchData>) => (
      <Typography variant="subtitle2">
        {parameters.row.price ? (
          `$${Number(parameters.row.price).toLocaleString()}`
        ) : (
          <Chip color="info" label="Private" size="small" />
        )}
      </Typography>
    ),
    type: "number",
    valueGetter: (value: string) => (value ? Number.parseFloat(value.replaceAll(/[$,]/g, "")) : "-"),
    width: 150,
  },
  {
    field: "universityName",
    headerName: "University Name",
    renderCell: (parameters: GridRenderCellParams<ISponsoredResearchData>) => {
      if (parameters.row.universityName) {
        if (parameters.row.universityPreferredUrl) {
          return (
            <a href={parameters.row.universityPreferredUrl} rel="noopener noreferrer" target="_blank">
              {parameters.value}
            </a>
          );
        } else {
          return <span>{parameters.value}</span>;
        }
      } else return <span>-</span>;
    },
    width: 300,
  },
  {
    field: "companyName",
    headerName: "Partner Name",
    renderCell: (parameters: GridRenderCellParams<ISponsoredResearchData>) => {
      if (parameters.row.companyName) {
        if (parameters.row.companyPreferredUrl) {
          return (
            <a href={parameters.row.companyPreferredUrl} rel="noopener noreferrer" target="_blank">
              {parameters.value}
            </a>
          );
        } else {
          return <span>{parameters.value}</span>;
        }
      } else return <span>-</span>;
    },
    width: 300,
  },
  {
    field: "description",
    headerName: "Description",
    renderCell: (parameters: GridRenderCellParams) => (
      <span>{parameters.value !== "NaN" && parameters.value ? parameters.value : "-"}</span>
    ),
    sortable: false,
    width: 2000,
  },
];

export const secColumns: GridColDef[] = [
  {
    field: "fileDate",
    flex: 1,
    headerName: "File Date",
    renderCell: (parameters: GridRenderCellParams<ISecFilingsData>) => {
      if (parameters.row.fileDate) {
        return <Typography variant="subtitle2">{new Date(parameters.row.fileDate).toLocaleDateString()}</Typography>;
      }
      return <Typography variant="subtitle2">-</Typography>;
    },
    valueGetter: (value: string) => new Date(value),
  },
  {
    field: "type",
    flex: 1,
    headerName: "Type",
    renderCell: (parameters: GridRenderCellParams<ISecFilingsData>) => (
      <MuiLink href={parameters.row.filingUrl || ""} rel="noopener noreferrer" target="_blank">
        <Box alignItems="center" display="flex" gap={1}>
          <FileOpenRoundedIcon fontSize="small" />
          {parameters.value}
        </Box>
      </MuiLink>
    ),
    sortable: false,
  },
  {
    field: "accessionNumber",
    flex: 1,
    headerName: "Accession Number",
    renderCell: (parameters) => <Typography variant="subtitle2">{parameters.value}</Typography>,
  },
];

export const licensingColumns: GridColDef[] = [
  {
    field: "licensorName",
    headerName: "Licensor",
    renderCell: (parameters) => <Typography variant="subtitle2">{parameters.value}</Typography>,
    width: 300,
  },
  {
    field: "patentNumber",
    headerName: "Patent #",
    renderCell: (parameters) => <Typography variant="subtitle2">{parameters.value}</Typography>,
    sortable: false,
    width: 300,
  },
  {
    field: "reason",
    headerName: "Reason",
    renderCell: (parameters) => <Typography variant="subtitle2">{parameters.value}</Typography>,
    sortable: false,
    width: 400,
  },
  {
    field: "dealTerm",
    headerName: "Terms",
    renderCell: (parameters) => <Typography variant="subtitle2">{parameters.value}</Typography>,
    sortable: false,
    width: 400,
  },
];

export const grantsDataColumns = (handleCellClick: (argument1: string, argument2: string) => void): GridColDef[] => [
  {
    field: "abstract",
    headerName: "Abstract Details",
    renderCell: (parameters: GridRenderCellParams<IGrantsData>) => (
      <MuiLink
        component="button"
        onClick={() => parameters.row.abstract && handleCellClick("Abstract Details", parameters.row.abstract)}
        style={{ alignItems: "center", display: "flex", gap: "8px" }}
      >
        <TextSnippetRoundedIcon fontSize="small" /> View Abstract
      </MuiLink>
    ),
    width: 170,
  },
  {
    field: "awardYear",
    headerName: "Year",
    renderCell: (parameters: GridRenderCellParams<IGrantsData>) => (
      <Typography variant="subtitle2">{parameters.value}</Typography>
    ),
    valueGetter: (value: string) => Number.parseInt(value, 10),
    width: 100,
  },
  {
    field: "awardAmount",
    headerName: "Amount",
    renderCell: (parameters: GridRenderCellParams<IGrantsData>) => (
      <Typography variant="subtitle2">
        {parameters.row.awardAmount ? (
          `$${Number(parameters.row.awardAmount).toLocaleString()}`
        ) : (
          <Chip color="info" label="Private" size="small" />
        )}
      </Typography>
    ),
    type: "number",
    valueFormatter: (value: string) => `$${value.toLocaleString()}`,
    valueGetter: (value: string) => Number.parseFloat(value.replaceAll(/[$,]/g, "")),
    width: 150,
  },
  {
    field: "phase",
    headerName: "Phase",
    renderCell: (parameters) => <Typography variant="subtitle2">{parameters.value}</Typography>,
    width: 100,
  },
  {
    field: "companyName",
    headerName: "Company",
    renderCell: (parameters) => <Typography variant="subtitle2">{parameters.value}</Typography>,
    width: 300,
  },
  {
    field: "agency",
    headerName: "Agency",
    renderCell: (parameters) => <Typography variant="subtitle2">{parameters.value}</Typography>,
    width: 320,
  },
  {
    field: "awardTitle",
    headerName: "Award Title",
    renderCell: (parameters) => (
      <Typography variant="subtitle2">
        {!parameters.value || parameters.value === "NaN" ? "-" : parameters.value}
      </Typography>
    ),
    width: 400,
  },
  {
    field: "branch",
    headerName: "Branch",
    renderCell: (parameters) => (
      <Typography variant="subtitle2">
        {!parameters.value || parameters.value === "NaN" ? "-" : parameters.value}
      </Typography>
    ),
    width: 350,
  },
  {
    field: "program",
    headerName: "Program",
    renderCell: (parameters) => <Typography variant="subtitle2">{parameters.value}</Typography>,
    width: 150,
  },
];
